/**
 * RenderPanel - AI Rendering UI Component
 *
 * BYK-6: Integrates with Gemini 3 Pro for photorealistic kitchen renders.
 * BYK-141: Multi-Turn Refinement with Thought Signatures
 *
 * Features:
 * - Style/camera/lighting selection
 * - Real-time render progress
 * - Render history gallery
 * - Download/share options
 * - Multi-turn refinement (BYK-141)
 * - Refinement lineage view (BYK-141)
 */

import { useState, useCallback, useEffect } from "react";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { cn } from "@/lib/utils";
import {
  createRender,
  listRenders,
  getRenderImageUrl,
  refineRender,
  getRenderLineage,
  canRefineRender,
  MAX_REFINEMENT_DEPTH,
  RENDER_STYLES,
  RENDER_CAMERAS,
  RENDER_LIGHTING,
  type Render,
  type RenderStyle,
  type RenderCamera,
  type RenderLighting,
  type RenderLineageResponse,
} from "@/services/renders";
import { getOrCreateDefaultProject } from "@/services/projects";

interface RenderPanelProps {
  onClose?: () => void;
  projectId?: string;
}

type RenderPhase = "idle" | "configuring" | "generating" | "complete" | "error" | "refining" | "refine-input";

export const RenderPanel: React.FC<RenderPanelProps> = ({ onClose: _onClose, projectId: propProjectId }) => {
  // Render configuration state
  const [style, setStyle] = useState<RenderStyle>("photorealistic");
  const [camera, setCamera] = useState<RenderCamera>("eye-level");
  const [lighting, setLighting] = useState<RenderLighting>("daylight");
  const [enableKashrut, setEnableKashrut] = useState(false);

  // Render process state
  const [phase, setPhase] = useState<RenderPhase>("idle");
  const [progress, setProgress] = useState(0);
  const [currentRender, setCurrentRender] = useState<Render | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Project state - auto-create if not provided
  const [projectId, setProjectId] = useState<string | null>(propProjectId || null);

  // History state
  const [renders, setRenders] = useState<Render[]>([]);
  const [selectedRender, setSelectedRender] = useState<Render | null>(null);

  // BYK-141: Refinement state
  const [refinementPrompt, setRefinementPrompt] = useState("");
  const [lineage, setLineage] = useState<RenderLineageResponse | null>(null);
  const [showLineage, setShowLineage] = useState(false);

  // Get design state for prompt building
  const { room, volumes, products, walls } = useFloorPlanStore();

  // Load render history on mount (only if we have a project)
  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;

    const fetchHistory = async () => {
      try {
        const result = await listRenders({ projectId, limit: 10 });
        if (!cancelled) {
          setRenders(result.items);
        }
      } catch (err) {
        console.error("Failed to load render history:", err);
      }
    };

    fetchHistory();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Refresh render history - used after generating new renders
  const refreshRenderHistory = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await listRenders({ projectId, limit: 10 });
      setRenders(result.items);
    } catch (err) {
      console.error("Failed to refresh render history:", err);
    }
  }, [projectId]);

  // Generate render
  const handleGenerate = useCallback(async () => {
    setPhase("generating");
    setProgress(0);
    setError(null);

    try {
      // Start progress animation
      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 5, 90));
      }, 500);

      // Get or create project if needed
      let currentProjectId = projectId;
      if (!currentProjectId) {
        setProgress(5);
        const project = await getOrCreateDefaultProject({
          room: { width: room.width, height: room.depth },
          volumes,
          walls,
        });
        currentProjectId = project.id;
        setProjectId(currentProjectId);
        setProgress(15);
      }

      // Create render request
      const response = await createRender({
        projectId: currentProjectId,
        style,
        cameraAngle: camera,
        lightingMood: lighting,
        enableKashrut,
        sync: true, // Wait for completion
      });

      clearInterval(progressInterval);
      setProgress(100);
      setCurrentRender(response.render);
      setPhase("complete");

      // Refresh history
      refreshRenderHistory();
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : "Render failed");
    }
  }, [projectId, style, camera, lighting, enableKashrut, room, volumes, walls, refreshRenderHistory]);

  // Download render
  const handleDownload = useCallback((render: Render) => {
    const url = getRenderImageUrl(render.id);
    const link = document.createElement("a");
    link.href = url;
    link.download = `kitchen-render-${render.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // BYK-141: Handle refinement
  const handleRefine = useCallback(async () => {
    if (!currentRender || !refinementPrompt.trim()) return;

    setPhase("refining");
    setProgress(0);
    setError(null);

    try {
      // Start progress animation
      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 5, 90));
      }, 500);

      const result = await refineRender(currentRender.id, {
        refinementPrompt: refinementPrompt.trim(),
        style,
        cameraAngle: camera,
        lightingMood: lighting,
      });

      clearInterval(progressInterval);
      setProgress(100);
      setCurrentRender(result);
      setRefinementPrompt("");
      setPhase("complete");

      // Refresh history
      refreshRenderHistory();
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : "Refinement failed");
    }
  }, [currentRender, refinementPrompt, style, camera, lighting, refreshRenderHistory]);

  // BYK-141: Load lineage for current render
  const loadLineage = useCallback(async (renderId: string) => {
    try {
      const result = await getRenderLineage(renderId);
      setLineage(result);
      setShowLineage(true);
    } catch (err) {
      console.error("Failed to load lineage:", err);
    }
  }, []);

  // Count placed items for summary
  const itemCount = volumes.length;
  const productNames = volumes
    .map((v) => products.find((p) => p.id === v.productId)?.name)
    .filter(Boolean)
    .slice(0, 3);

  return (
    <div className="flex h-full flex-col">
      {/* Design Summary */}
      <div className="mb-4 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50 p-3">
        <div className="text-xs font-medium text-purple-700">Current Design</div>
        <div className="mt-1 text-sm text-gray-700">
          {room.width / 100}m × {room.depth / 100}m kitchen
          {itemCount > 0 && (
            <span className="text-gray-500">
              {" "}
              • {itemCount} items
              {productNames.length > 0 && ` (${productNames.join(", ")}...)`}
            </span>
          )}
        </div>
        {walls.length === 0 && (
          <div className="mt-1 text-xs text-amber-600">
            ⚠️ Add walls for better renders
          </div>
        )}
      </div>

      {/* Configuration Section */}
      {(phase === "idle" || phase === "configuring") && (
        <>
          {/* Style Selection */}
          <div className="mb-4">
            <label className="mb-2 block text-xs font-medium text-gray-700">
              Render Style
            </label>
            <div className="grid grid-cols-2 gap-2">
              {RENDER_STYLES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  className={cn(
                    "rounded-lg border p-2 text-left text-xs transition-all",
                    style === s.id
                      ? "border-purple-500 bg-purple-50 ring-1 ring-purple-500"
                      : "border-gray-200 hover:border-gray-300"
                  )}
                >
                  <div className="font-medium text-gray-900">{s.label}</div>
                  <div className="mt-0.5 text-gray-500 line-clamp-2">
                    {s.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Camera Angle */}
          <div className="mb-4">
            <label className="mb-2 block text-xs font-medium text-gray-700">
              Camera Angle
            </label>
            <div className="flex flex-wrap gap-1">
              {RENDER_CAMERAS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCamera(c.id)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    camera === c.id
                      ? "bg-purple-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lighting */}
          <div className="mb-4">
            <label className="mb-2 block text-xs font-medium text-gray-700">
              Lighting
            </label>
            <div className="flex flex-wrap gap-1">
              {RENDER_LIGHTING.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setLighting(l.id)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    lighting === l.id
                      ? "bg-amber-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Kashrut Mode Toggle */}
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <label className="flex cursor-pointer items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-blue-900">
                  <span>✡️</span>
                  Kashrut-Compliant Layout
                </div>
                <div className="mt-0.5 text-xs text-blue-700">
                  Dual zones for meat & dairy with 60cm separation
                </div>
              </div>
              <button
                onClick={() => setEnableKashrut(!enableKashrut)}
                className={cn(
                  "relative h-6 w-11 rounded-full transition-colors",
                  enableKashrut ? "bg-blue-600" : "bg-gray-300"
                )}
              >
                <span
                  className={cn(
                    "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
                    enableKashrut && "translate-x-5"
                  )}
                />
              </button>
            </label>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={itemCount === 0}
            className={cn(
              "w-full rounded-lg py-3 text-sm font-semibold transition-all",
              itemCount > 0
                ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg hover:shadow-xl active:scale-[0.98]"
                : "cursor-not-allowed bg-gray-200 text-gray-500"
            )}
          >
            {itemCount === 0 ? (
              "Add products to render"
            ) : (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                Generate AI Render
              </span>
            )}
          </button>
        </>
      )}

      {/* Generating State */}
      {phase === "generating" && (
        <div className="flex flex-1 flex-col items-center justify-center py-8">
          {/* Animated progress ring */}
          <div className="relative mb-4">
            <svg className="h-20 w-20 -rotate-90 transform">
              <circle
                cx="40"
                cy="40"
                r="36"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="40"
                cy="40"
                r="36"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                strokeDasharray={226}
                strokeDashoffset={226 - (226 * progress) / 100}
                className="text-purple-600 transition-all duration-500"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-semibold text-purple-600">
                {progress}%
              </span>
            </div>
          </div>

          <div className="text-center">
            <div className="font-medium text-gray-900">Generating Render...</div>
            <div className="mt-1 text-xs text-gray-500">
              {progress < 30 && "Analyzing your design..."}
              {progress >= 30 && progress < 60 && "Building AI prompt..."}
              {progress >= 60 && progress < 90 && "Creating photorealistic image..."}
              {progress >= 90 && "Finalizing render..."}
            </div>
          </div>

          {/* Cancel button */}
          <button
            onClick={() => setPhase("idle")}
            className="mt-6 text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}

      {/* BYK-141: Refine Input State */}
      {phase === "refine-input" && currentRender && (
        <div className="flex flex-1 flex-col">
          {/* Current render preview (small) */}
          <div className="relative mb-4 overflow-hidden rounded-lg bg-gray-100">
            <img
              src={getRenderImageUrl(currentRender.id)}
              alt="Current render"
              className="aspect-[4/3] w-full object-cover opacity-60"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-purple-900/50 to-transparent">
              <div className="text-center text-white">
                <div className="text-lg font-semibold">Refining Render</div>
                <div className="text-sm opacity-80">
                  Iteration {(currentRender.refinementDepth || 0) + 1} of {MAX_REFINEMENT_DEPTH}
                </div>
              </div>
            </div>
          </div>

          {/* Refinement prompt input */}
          <div className="mb-4">
            <label className="mb-2 block text-xs font-medium text-gray-700">
              What would you like to change?
            </label>
            <textarea
              value={refinementPrompt}
              onChange={(e) => setRefinementPrompt(e.target.value)}
              placeholder="e.g., Make the cabinets darker oak, add under-cabinet lighting, change the countertop to marble..."
              className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              rows={3}
              maxLength={2000}
            />
            <div className="mt-1 text-right text-xs text-gray-400">
              {refinementPrompt.length}/2000
            </div>
          </div>

          {/* Optional style overrides */}
          <details className="mb-4">
            <summary className="cursor-pointer text-xs font-medium text-gray-600 hover:text-gray-800">
              Adjust style settings (optional)
            </summary>
            <div className="mt-3 space-y-3 rounded-lg bg-gray-50 p-3">
              {/* Camera */}
              <div>
                <div className="mb-1 text-xs text-gray-500">Camera Angle</div>
                <div className="flex flex-wrap gap-1">
                  {RENDER_CAMERAS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setCamera(c.id)}
                      className={cn(
                        "rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
                        camera === c.id
                          ? "bg-purple-600 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Lighting */}
              <div>
                <div className="mb-1 text-xs text-gray-500">Lighting</div>
                <div className="flex flex-wrap gap-1">
                  {RENDER_LIGHTING.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setLighting(l.id)}
                      className={cn(
                        "rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
                        lighting === l.id
                          ? "bg-amber-500 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </details>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setPhase("complete");
                setRefinementPrompt("");
              }}
              className="flex-1 rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleRefine}
              disabled={!refinementPrompt.trim()}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-all",
                refinementPrompt.trim()
                  ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg hover:shadow-xl"
                  : "cursor-not-allowed bg-gray-200 text-gray-500"
              )}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
              Refine Render
            </button>
          </div>
        </div>
      )}

      {/* BYK-141: Refining State */}
      {phase === "refining" && (
        <div className="flex flex-1 flex-col items-center justify-center py-8">
          {/* Animated progress ring */}
          <div className="relative mb-4">
            <svg className="h-20 w-20 -rotate-90 transform">
              <circle
                cx="40"
                cy="40"
                r="36"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="40"
                cy="40"
                r="36"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                strokeDasharray={226}
                strokeDashoffset={226 - (226 * progress) / 100}
                className="text-purple-600 transition-all duration-500"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-semibold text-purple-600">
                {progress}%
              </span>
            </div>
          </div>

          <div className="text-center">
            <div className="font-medium text-gray-900">Refining Render...</div>
            <div className="mt-1 text-xs text-gray-500">
              {progress < 30 && "Applying your changes..."}
              {progress >= 30 && progress < 60 && "Preserving context from original..."}
              {progress >= 60 && progress < 90 && "Generating refined image..."}
              {progress >= 90 && "Finalizing refinement..."}
            </div>
            {refinementPrompt && (
              <div className="mx-auto mt-3 max-w-xs rounded bg-purple-50 p-2 text-xs text-purple-700">
                "{refinementPrompt}"
              </div>
            )}
          </div>

          {/* Cancel button */}
          <button
            onClick={() => setPhase("complete")}
            className="mt-6 text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Complete State */}
      {phase === "complete" && currentRender && (
        <div className="flex flex-1 flex-col">
          {/* Render preview */}
          <div className="relative mb-4 overflow-hidden rounded-lg bg-gray-100">
            <img
              src={getRenderImageUrl(currentRender.id)}
              alt="Generated kitchen render"
              className="aspect-[4/3] w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='14' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle'%3EImage loading...%3C/text%3E%3C/svg%3E";
              }}
            />
            <div className="absolute bottom-2 right-2 rounded bg-black/50 px-2 py-1 text-xs text-white">
              {style} • {camera}
            </div>
            {/* BYK-141: Refinement depth indicator */}
            {(currentRender.refinementDepth || 0) > 0 && (
              <div className="absolute left-2 top-2 rounded bg-purple-600/80 px-2 py-1 text-xs text-white">
                Refinement {currentRender.refinementDepth}/{MAX_REFINEMENT_DEPTH}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => handleDownload(currentRender)}
              className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </button>
            <button
              onClick={() => {
                setPhase("idle");
                setCurrentRender(null);
                setLineage(null);
                setShowLineage(false);
              }}
              className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-purple-600 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              New Render
            </button>
          </div>

          {/* BYK-141: Refine Button */}
          {canRefineRender(currentRender) && (
            <button
              onClick={() => setPhase("refine-input")}
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 py-3 text-sm font-medium text-purple-700 transition-colors hover:border-purple-400 hover:bg-purple-100"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Refine This Render
              <span className="text-xs text-purple-500">
                ({(currentRender.refinementDepth || 0) + 1}/{MAX_REFINEMENT_DEPTH})
              </span>
            </button>
          )}

          {/* BYK-141: Lineage toggle */}
          {(currentRender.parentRenderId || (currentRender.refinementDepth || 0) > 0) && (
            <button
              onClick={() => {
                if (showLineage) {
                  setShowLineage(false);
                } else {
                  loadLineage(currentRender.id);
                }
              }}
              className="mb-4 text-xs text-purple-600 hover:text-purple-800"
            >
              {showLineage ? "Hide refinement history" : "Show refinement history"}
            </button>
          )}

          {/* BYK-141: Lineage View */}
          {showLineage && lineage && (
            <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-3">
              <div className="mb-2 text-xs font-medium text-purple-800">Refinement History</div>
              <div className="space-y-2">
                {/* Root render */}
                {lineage.ancestors.length > 0 && (
                  <div
                    className="flex cursor-pointer items-center gap-2 rounded bg-white p-2 text-xs hover:bg-purple-100"
                    onClick={() => {
                      const rootId = lineage.rootRenderId;
                      const rootRender = renders.find(r => r.id === rootId);
                      if (rootRender) {
                        setCurrentRender(rootRender);
                        setShowLineage(false);
                        setLineage(null);
                      }
                    }}
                  >
                    <div className="h-8 w-8 rounded bg-gray-200">
                      <img
                        src={getRenderImageUrl(lineage.rootRenderId)}
                        alt="Original"
                        className="h-full w-full rounded object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-700">Original Render</div>
                    </div>
                  </div>
                )}
                {/* Ancestors */}
                {lineage.ancestors.slice(1).map((ancestor, i) => (
                  <div
                    key={ancestor.id}
                    className="flex cursor-pointer items-center gap-2 rounded bg-white p-2 text-xs hover:bg-purple-100"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-purple-200 text-purple-700">
                      {i + 1}
                    </div>
                    <div className="flex-1 truncate">
                      <div className="truncate text-gray-600">{ancestor.refinementPrompt}</div>
                    </div>
                  </div>
                ))}
                {/* Current */}
                <div className="flex items-center gap-2 rounded bg-purple-200 p-2 text-xs">
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-purple-600 text-white">
                    {lineage.current.refinementDepth}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-purple-800">Current</div>
                    {lineage.current.refinementPrompt && (
                      <div className="truncate text-purple-600">{lineage.current.refinementPrompt}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success message */}
          <div className="rounded-lg bg-green-50 p-3 text-center text-sm text-green-700">
            ✅ Render complete! {canRefineRender(currentRender) ? "Refine it or " : ""}Download or generate a new one.
          </div>
        </div>
      )}

      {/* Error State */}
      {phase === "error" && (
        <div className="flex flex-1 flex-col items-center justify-center py-8">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="text-center">
            <div className="font-medium text-gray-900">Render Failed</div>
            <div className="mt-1 text-sm text-red-600">{error}</div>
          </div>
          <button
            onClick={() => {
              setPhase("idle");
              setError(null);
            }}
            className="mt-4 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Render History */}
      {renders.length > 0 && phase !== "generating" && (
        <div className="mt-4 border-t pt-4">
          <div className="mb-2 text-xs font-medium text-gray-700">
            Recent Renders
          </div>
          <div className="grid grid-cols-3 gap-2">
            {renders.slice(0, 6).map((render) => (
              <button
                key={render.id}
                onClick={() => {
                  setSelectedRender(render);
                  setCurrentRender(render);
                  setPhase("complete");
                }}
                className={cn(
                  "group relative aspect-square overflow-hidden rounded-lg bg-gray-100",
                  selectedRender?.id === render.id && "ring-2 ring-purple-500"
                )}
              >
                {render.status === "completed" ? (
                  <img
                    src={getRenderImageUrl(render.id)}
                    alt="Previous render"
                    className="h-full w-full object-cover transition-transform group-hover:scale-110"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-gray-400">
                    {render.status === "processing" ? "..." : "×"}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RenderPanel;
